//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import Avatar from '@components/atoms/avatar/Avatar';
import Icon from '@components/atoms/icon/Icon';
import {
  BodyBoldText,
  BodySecondaryText,
  BodyText,
} from '@components/atoms/text/Text';
import { SocialNetwork } from '@models/SocialNetwork';
import { ContentSourceBadge } from '@uia/details/badge/ContentSourceBadge';
import { isDefined } from '@utils/filterPredicates';
import { readableNumber } from '@utils/readableNumber';
import { pluralize } from '@utils/stringUtils';

export enum ContentSourceType {
  HASHTAG = 'hashtag',
  USER = 'user',
  UPLOAD = 'upload',
}

export interface ContentSourceBadgeAvatarProps {
  avatarUrl?: string;
  followerCount?: number;
  mainTitle: string;
  isRouteToExternal: boolean;
  socialNetwork: SocialNetwork;
  sourceType: ContentSourceType;
  subTitle?: string;
}

export const ContentSourceBadgeAvatar = ({
  avatarUrl,
  followerCount,
  isRouteToExternal,
  mainTitle,
  socialNetwork,
  sourceType,
  subTitle,
}: ContentSourceBadgeAvatarProps) => (
  <AvatarWrapper>
    {(() => {
      if (sourceType === ContentSourceType.UPLOAD) {
        return <Avatar username={mainTitle} />;
      } else if (isDefined(avatarUrl)) {
        return <Avatar imageUrl={avatarUrl} />;
      } else {
        return <Avatar iconName={ContentSourceType.USER} />;
      }
    })()}
    <LabelsWrapper>
      <LabelText title={mainTitle}>
        <BodyText>{mainTitle}</BodyText>
        {isRouteToExternal && <LinkOutIcon name='linkout' />}
      </LabelText>
      {sourceType !== ContentSourceType.UPLOAD && isDefined(subTitle) && (
        <SubTitleText title={subTitle}>{subTitle}</SubTitleText>
      )}
      {isDefined(followerCount) && (
        <BodyText>
          {`${readableNumber(followerCount)} ${pluralize(followerCount, socialNetwork === SocialNetwork.YouTube ? 'Subscriber' : 'Follower', undefined, false)}`}
        </BodyText>
      )}
    </LabelsWrapper>
  </AvatarWrapper>
);

const AvatarWrapper = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${theme.spacings[3]};
  `,
);

const LabelsWrapper = styled.div(
  () => `
    display: flex;
    flex-direction: column;
  `,
);

const LabelText = styled(BodyBoldText)(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spacings[2]};
  `,
);

const SubTitleText = styled(BodySecondaryText)(
  () => `
    ${ContentSourceBadge}:hover {
      text-decoration: underline;
    }
  `,
);

const LinkOutIcon = styled(Icon)(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: calc(${theme.spacings[2]} + ${theme.spacings[1]});
    width: calc(${theme.spacings[2]} + ${theme.spacings[1]});
    color: ${theme.colors.gray4};
  `,
);
